'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className = '' }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2026);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    products: [
      { label: 'KnowMax', href: '/product-suite-overview#knowmax' },
      { label: 'PhygitalMax', href: '/product-suite-overview#phygitalmax' },
      { label: 'LeapMax', href: '/product-suite-overview#leapmax' },
      { label: 'DeviceMax', href: '/product-suite-overview#devicemax' },
    ],
    solutions: [
      { label: 'Telecommunications', href: '/industry-solutions#telecom' },
      { label: 'Banking & Finance', href: '/industry-solutions#banking' },
      { label: 'Retail & E-commerce', href: '/industry-solutions#retail' },
    ],
    company: [
      { label: 'About Us', href: '#' },
      { label: 'Careers', href: '#' },
      { label: 'Contact', href: '#' },
      { label: 'Partners', href: '#' },
    ],
    resources: [
      { label: 'Documentation', href: '#' },
      { label: 'Case Studies', href: '#' },
      { label: 'Blog', href: '#' },
      { label: 'Support', href: '#' },
    ],
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: 'LinkIcon', href: '#' },
    { name: 'Twitter', icon: 'AtSymbolIcon', href: '#' },
    { name: 'YouTube', icon: 'PlayIcon', href: '#' },
    { name: 'GitHub', icon: 'CodeBracketIcon', href: '#' },
  ];

  if (!isHydrated) {
    return (
      <footer className={`bg-[#000] text-primary-foreground pt-16 pb-8 ${className}`}>
        <div className="container mx-auto px-4">
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`bg-[#000] text-primary-foreground pt-16 pb-8 ${className}`}>
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-2 space-y-4">
            <Link href="/homepage" className="flex items-center space-x-3 group">
              <div className="relative">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="transition-transform duration-300 group-hover:scale-105"
                >
                  <rect width="40" height="40" rx="8" fill="white" fillOpacity="0.1" />
                  <path
                    d="M20 8L28 14V26L20 32L12 26V14L20 8Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20 14L24 17V23L20 26L16 23V17L20 14Z"
                    fill="white"
                    opacity="0.8"
                  />
                  <circle cx="20" cy="20" r="2" fill="white" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="font-display font-bold text-2xl leading-none">UnifyMax</span>
                <span className="font-body text-sm opacity-80 leading-none mt-1">Enterprise</span>
              </div>
            </Link>
            <p className="font-body text-sm opacity-80 leading-relaxed">
              Transforming customer engagement with 25+ years of expertise and AI-powered intelligence.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-colors duration-300"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} variant="outline" />
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-display font-semibold text-base mb-4">Products</h3>
            <ul className="space-y-2">
              {footerLinks.products.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-body text-sm opacity-80 hover:opacity-100 hover:text-primary transition-opacity duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="font-display font-semibold text-base mb-4">Solutions</h3>
            <ul className="space-y-2">
              {footerLinks.solutions.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-body text-sm opacity-80 hover:opacity-100 hover:text-primary  transition-opacity duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-display font-semibold text-base mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-body text-sm opacity-80 hover:opacity-100 hover:text-primary  transition-opacity duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-display font-semibold text-base mb-4">Resources</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="font-body text-sm opacity-80 hover:opacity-100 hover:text-primary  transition-opacity duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="font-body text-sm opacity-80">
              © {currentYear} UnifyMax Enterprise. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              <Link href="/privacy" className="font-body text-sm opacity-80 hover:opacity-100 hover:text-primary  transition-opacity duration-300">
                Privacy Policy
              </Link>
              <Link href="/terms" className="font-body text-sm opacity-80 hover:opacity-100 hover:text-primary  transition-opacity duration-300">
                Terms of Service
              </Link>
              <Link href="/security" className="font-body text-sm opacity-80 hover:opacity-100 hover:text-primary  transition-opacity duration-300">
                Security
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;